import { Paper, Stack } from "@mui/material";
import { ComponentsCommonHeader } from "components/ComponentsCommonHeader";
import { GeneralErrorBoundary } from "components/ErrorBoundary";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { hasInProgressJobs } from "features/dt-plus-setup/lib/processingJob";
import { LastActionTimeTracker } from "features/shared/components/LastActionTimeTracker";
import { eventNames, trackGAEvent } from "functions/googleAnalytics";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import type { IfcPipelineJob } from "../../model/types";

import { useGetExecutionHistory } from "../../api/ifcPipeline";
import { ExecutionStepsModal } from "../steps/ExecutionStepsModal";
import { ExecutionHistoryTable } from "./ExecutionHistoryTable";

export const ExecutionHistoryCard = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { projectId } = useParams();

  const enabled = !!projectId;
  const { data, isFetching, isLoading } = useGetExecutionHistory(projectId, enabled);

  const [selectedJobId, setSelectedJobId] = useState<null | string>(null);

  const handleRowClick = (item: IfcPipelineJob) => {
    trackGAEvent(eventNames.EXECUTION_STEPS_VIEW, { executionId: item.id });
    setSelectedJobId(item.id);
  };

  const handleModalClose = () => setSelectedJobId(null);

  const selectedJob = useMemo(() => {
    return data?.find((job) => job.id === selectedJobId);
  }, [data, selectedJobId]);

  return (
    <GeneralErrorBoundary>
      <Paper className={className}>
        <ComponentsCommonHeader
          title={
            <Stack className="gap-2">
              <span>{t("Execution.history.title") as string}</span>
              {hasInProgressJobs(data) && <LastActionTimeTracker triggered={!isFetching} />}
            </Stack>
          }
        />
        <p className="mt-1 text-lds-foreground-muted font-ds-body-regular-xs">{t("Execution.history.description")}</p>
        <LoadingOrEmptyWrapper
          height="20rem"
          showEmpty={!isLoading && (data?.length ?? 0) === 0}
          showLoading={isLoading}
        >
          <ExecutionHistoryTable className="mt-6" items={data ?? []} onRowClick={handleRowClick} />
        </LoadingOrEmptyWrapper>
        <ExecutionStepsModal onClose={handleModalClose} selectedJob={selectedJob} />
      </Paper>
    </GeneralErrorBoundary>
  );
};
